import { useRef } from 'react';
import { useGame } from '../state/GameContext';
import { useGameActions } from '../hooks/useGameActions';
import ShareButton from '../ShareButton';

export default function WaitingRoom() {
  const { state, dispatch } = useGame();
  const actions = useGameActions();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Always reset before opening
      fileInputRef.current.click();
    }
  };

  return (
    <div className="waiting-room">
      <h2 className="text-2xl mb-2">Room: {state.roomCode}</h2>
      <p className="text-sm mb-3 opacity-90">Share this code with your friends!</p>

      <div className="mb-5 flex justify-center">
        <ShareButton roomCode={state.roomCode} />
      </div>

      {/* Collapsible Players List */}
      <div className="players-list-collapsible">
        <button
          onClick={() => dispatch({ type: 'SET_FIELD', field: 'showPlayersList', value: !state.showPlayersList })}
          className="w-full px-4 py-3 bg-white/12 border border-white/20 rounded-xl text-white font-semibold text-base cursor-pointer flex justify-between items-center transition-all duration-300 hover:bg-white/18"
        >
          <span>Players ({state.gameState.players.length}) - {state.gameState.totalPhotos} photos</span>
          <span
            className="text-xl transition-transform duration-300"
            style={{ transform: state.showPlayersList ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            ▼
          </span>
        </button>

        {state.showPlayersList && (
          <div className="players-list mt-2 animate-[slideDown_0.3s_ease]">
            {state.gameState.players.map((player) => (
              <div key={player.id} className="player-item">
                <span>
                  {player.name}
                  {player.isHost && <span className="host-badge">Host</span>}
                </span>
                <span>{player.photosUploaded || 0} photos</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden file input — lives outside conditional rendering so it never unmounts */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={actions.handleMultipleFileSelect}
        className="hidden-input"
        multiple
      />

      <div className="photo-upload mt-4">
        <h3 className="text-lg mb-3">Upload Your Photos</h3>

        <div
          onDrop={actions.handleDrop}
          onDragOver={actions.handleDragOver}
          onClick={openFilePicker}
          className="photo-upload-zone cursor-pointer p-5 border-2 border-dashed border-white/30 rounded-xl text-center transition-all duration-300 hover:border-white/60 hover:bg-white/5"
          style={{ pointerEvents: state.loading ? 'none' : 'auto', opacity: state.loading ? 0.5 : 1 }}
        >
          <p className="text-center m-0 text-sm font-semibold">
            Click here or drag & drop photos
          </p>
        </div>

        {state.loading && state.uploadProgress > 0 && (
          <div className="mt-3">
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                style={{ width: `${state.uploadProgress}%` }}
              />
            </div>
            <p className="text-center mt-2 text-sm">
              Uploading... {state.uploadProgress}%
            </p>
          </div>
        )}

        {state.uploadedPhotos.length > 0 && !state.loading && (
          <div className="mt-3 text-sm">
            <p>Uploaded: {state.uploadedPhotos.length} photos</p>
          </div>
        )}
      </div>

      {/* Total Photos Display */}
      <div className="mt-4 p-3 bg-white/8 rounded-xl text-center text-base font-semibold">
        Total Photos: {state.gameState.totalPhotos}
      </div>

      {/* Action Buttons */}
      <div className="mt-4 flex flex-col gap-3">
        {state.isHost && (
          <>
            <button
              className="button button-primary w-full"
              onClick={actions.startGame}
              disabled={state.loading || !state.gameState.canStartGame}
              style={{ opacity: state.gameState.canStartGame ? 1 : 0.6 }}
            >
              {state.loading ? 'Starting...' : 'Start Game!'}
            </button>
            {!state.gameState.canStartGame && (
              <p className="text-sm text-white/70 text-center m-0">
                Need at least 2 players and 5 photos total to start
              </p>
            )}
          </>
        )}

        {!state.isHost && (
          <div className="p-3 bg-white/8 rounded-xl text-center text-sm opacity-90">
            Waiting for host to start...
          </div>
        )}

        <button
          className="button button-secondary w-full"
          onClick={actions.resetGame}
        >
          Leave Game
        </button>
      </div>
    </div>
  );
}
